import { Injectable } from '@angular/core';
import { Company, GetCompanyPayload, User } from '@interfaces';
import { Actions, ofType } from '@ngrx/effects';

import { Store } from '@ngrx/store';

import { AppState } from '@state';
import { Observable } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import * as companyActions from './company.actions';
import * as companySelectors from './company.selectors';
import { CreateManagerOpts, SearchManagersOfCompanyOpts, UpdateManagerOpts } from 'src/app/_interfaces/manager';

@Injectable()
export class CompanyFacade {
  public companies$ = this.store.select(companySelectors.selectCompanies);
  public loadedCompany$ = this.store.select(companySelectors.selectCompany);
  public pagination$ = this.store.select(companySelectors.selectPagination);
  public loading$ = this.store.select(companySelectors.selectLoading);
  public errorMessage$ = this.store.select(companySelectors.selectErrorMessage);

  public companyEmployeesProcesses$ = this.store.select(companySelectors.selectEmployeesProcesses);
  public companyEmployeesProcessesPagination$ = this.store.select(companySelectors.selectEmployeesProcessesPagination);

  public getCompanySuccess$ = this.actions.pipe(ofType(companyActions.getCompanySuccess));
  public getCompanyError$ = this.actions.pipe(ofType(companyActions.getCompanyError));

  public getCompanyBasicDataSuccess$ = this.actions.pipe(ofType(companyActions.getCompanyBasicDataSuccess));
  public getCompanyBasicDataError$ = this.actions.pipe(ofType(companyActions.getCompanyBasicDataError));

  public saveSuccess$ = this.actions.pipe(
    ofType(companyActions.updateCompanySuccess, companyActions.createCompanySuccess),
    map(
      (result: { updatedCompany?: Company; createdCompany?: Company }) => result.updatedCompany || result.createdCompany
    )
  );

  public updateError$ = this.actions.pipe(ofType(companyActions.updateCompanyError));

  public managers$ = this.store.select(companySelectors.selectManagers);
  public managersPagination$ = this.store.select(companySelectors.selectManagersPagination);
  public loadingManagersList$ = this.store.select(companySelectors.selectManagersListLoading);

  public managerDetails$ = this.store.select(companySelectors.selectLoadedManagerDetails);
  public loadingManagerDetails$ = this.store.select(companySelectors.selectLoadingManagerDetails);
  public addManagedCompanySuccess$ = this.actions.pipe(ofType(companyActions.addManagedCompanySuccess));
  public removeManagedCompanySuccess$ = this.actions.pipe(ofType(companyActions.removeManagedCompanySuccess));

  public loadingCompanyOfSelfRegistered$ = this.store.select(companySelectors.selectLoadingCompanyOfSelfRegistered);
  public companyOfSelfRegistered$ = this.store.select(companySelectors.selectCompanyOfSelfRegistered);
  public allActiveCompanies$ = this.store.select(companySelectors.selectAllActiveCompanies);

  constructor(
    private store: Store<AppState>,
    private actions: Actions
  ) {}

  public getCompanies(payload: GetCompanyPayload): void {
    this.store.dispatch(companyActions.getCompanies({ payload }));
  }

  public getCompaniesBasicDataList(payload: GetCompanyPayload): void {
    this.store.dispatch(companyActions.getCompaniesBasicDataList({ payload }));
  }

  public getCompanyBasicData(payload: { id: string }): void {
    this.store.dispatch(companyActions.getCompanyBasicData({ payload }));
  }

  public getCompany(companyId: string): void {
    this.store.dispatch(companyActions.getCompany({ companyId }));
  }

  public resetLoadedCompany(): void {
    this.store.dispatch(companyActions.resetLoadedComapny());
  }

  public createCompany(company: Company): void {
    this.store.dispatch(companyActions.createCompany({ company }));
  }

  public updateCompany(companyId: string, company: Company): void {
    this.store.dispatch(companyActions.updateCompany({ companyId, company }));
  }

  public setIsActiveCompany(companyId: string, isActive: boolean): void {
    this.store.dispatch(companyActions.setIsActiveCompany({ companyId, isActive }));
  }

  // This one will always return Company (because of filter operator)
  // but fkin typescript and its strict null checks are the worst
  public getCompany$(companyId: string): Observable<Company | null> {
    return this.store.select(companySelectors.selectCompany).pipe(
      tap(company => {
        if (!company || company.id !== companyId) {
          this.getCompany(companyId);
        }
      }),
      filter(company => {
        if (!company || company.id !== companyId) {
          return false;
        }
        return true;
      })
    );
  }

  public getManagerDetails$(managerId: string): Observable<Partial<User> | null> {
    return this.store.select(companySelectors.selectLoadedManagerDetails).pipe(
      tap(manager => {
        if (manager?.id !== managerId) {
          this.getManagerDetails(managerId);
        }
      }),
      filter(manager => {
        if (manager?.id !== managerId) {
          return false;
        }
        return true;
      })
    );
  }

  public searchManagersOfCompany(payload: SearchManagersOfCompanyOpts): void {
    this.store.dispatch(companyActions.searchManagersOfCompany({ payload }));
  }

  public createManager(payload: CreateManagerOpts): void {
    this.store.dispatch(companyActions.createManager({ payload }));
  }

  public getManagerDetails(managerId: string): void {
    this.store.dispatch(companyActions.getManagerDetails({ managerId }));
  }

  public resendManagerActivation(email: string, userId: string): void {
    this.store.dispatch(companyActions.resendManagerActivation({ email, userId }));
  }

  public updateManager(payload: UpdateManagerOpts, userId: string): void {
    this.store.dispatch(companyActions.updateManager({ payload, userId }));
  }
  public softRemoveManager(managerId: string): void {
    this.store.dispatch(companyActions.softRemoveManager({ managerId }));
  }
  public restoreManager(managerId: string): void {
    this.store.dispatch(companyActions.restoreManager({ managerId }));
  }

  public addManagedCompany(opts: { managerId: string; companyId: string }) {
    this.store.dispatch(companyActions.addManagedCompany(opts));
  }
  public removeManagedCompany(opts: { mocId: string; managerId: string }) {
    this.store.dispatch(companyActions.removeManagedCompany(opts));
  }

  public getCompanyOfSelfRegistered(): void {
    this.store.dispatch(companyActions.getCompanyOfSelfRegistered());
  }

  public getAllActiveCompanies(): void {
    this.store.dispatch(companyActions.getAllActiveCompanies());
  }
}
